<template>
  <div
    class="tw-flex tw-items-center tw-gap-3"
    data-testid="sf-metric-controls"
  >
    <!-- The design system has no toggle-in-a-button, so the label borrows the
         button's own classes to sit flush with the pin button next to it.
         aria-disabled drives the shared disabled styling, which sets
         pointer-events: none, so the tooltip hangs on the wrapper.

         tw-flex on that wrapper is load-bearing: .sf-button is inline-flex, so
         in an inline wrapper it aligns on a baseline taken from its first
         child. The button's is its label text and this one's is the toggle,
         which has none, and the two land a pixel apart. -->
    <span
      v-if="showMainChart"
      v-tooltip="mainChartDisabledTooltipBinding"
      class="tw-flex"
    >
      <label
        class="sf-button sf-button--secondary !tw-gap-1.5 tw-m-0"
        :class="[
          `sf-button--${size}`,
          mainChartDisabled ? '' : 'tw-cursor-pointer',
        ]"
        :aria-disabled="mainChartDisabled ? 'true' : undefined"
        data-testid="sf-metric-controls-main-chart"
      >
        <SfToggle
          :model-value="mainChart"
          :icons="false"
          :disabled="mainChartDisabled"
          :aria-label="mainChartLabel"
          @update:model-value="onMainChartToggle"
        />
        <span class="sf-button__label">{{ mainChartLabel }}</span>
      </label>
    </span>

    <span
      v-if="showPin"
      v-tooltip="pinnedDisabledTooltipBinding"
      class="tw-flex"
    >
      <SfButtonDs
        :size="size"
        variant="secondary"
        :disabled="pinnedDisabled"
        data-testid="sf-metric-controls-pin"
        @click="togglePinned"
      >
        <FontAwesomeIcon
          v-if="pinned"
          class="tw-mr-0.5"
          :icon="['fas', 'thumbtack-slash']"
        />
        <i v-else class="fa-solid fa-thumbtack tw-mr-0.5" />
        {{ pinned ? unpinLabel : pinLabel }}
      </SfButtonDs>
    </span>
  </div>
</template>

<script lang="ts">
// thumbtack-slash is missing from the app's Font Awesome webfont, so the
// unpinned glyph is rendered through FontAwesomeIcon. A plain <script> block
// runs once at module load rather than once per instance.
import { library } from '@fortawesome/fontawesome-svg-core'
import { faThumbtackSlash } from '@fortawesome/free-solid-svg-icons'

library.add(faThumbtackSlash)
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'

defineOptions({
  name: 'SfMetricControls',
})

interface Props {
  mainChart: boolean
  // lg matches the height of a text input beside it; md suits a dense row.
  size?: 'md' | 'lg'
  pinned: boolean
  showMainChart?: boolean
  showPin?: boolean
  mainChartDisabled?: boolean
  mainChartDisabledTooltip?: string
  pinnedDisabled?: boolean
  pinnedDisabledTooltip?: string
  mainChartLabel?: string
  pinLabel?: string
  unpinLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  showMainChart: true,
  showPin: true,
  mainChartDisabled: false,
  mainChartDisabledTooltip: undefined,
  pinnedDisabled: false,
  pinnedDisabledTooltip: undefined,
  mainChartLabel: undefined,
  pinLabel: undefined,
  unpinLabel: undefined,
})

const emit =
  defineEmits<{
    'update:mainChart': [value: boolean]
    'update:pinned': [value: boolean]
  }>()

const mainChartDisabledTooltipBinding = computed(() =>
  props.mainChartDisabled ? props.mainChartDisabledTooltip ?? '' : ''
)

const pinnedDisabledTooltipBinding = computed(() =>
  props.pinnedDisabled ? props.pinnedDisabledTooltip ?? '' : ''
)

function onMainChartToggle(next: boolean): void {
  if (props.mainChartDisabled) return
  emit('update:mainChart', next)
}

function togglePinned(): void {
  if (props.pinnedDisabled) return
  emit('update:pinned', !props.pinned)
}
</script>
